/* ------------------------------------------------------------------
 * Product page gallery layout
 *
 * Desktop (>= 992px): builds a vertical thumbnail rail on the left of
 * the main product image. The rail's height always matches the main
 * image's own height (not a fixed thumbnail count) - if more thumbnails
 * exist than fit, the rail scrolls internally and a soft fade appears
 * at its bottom edge as a "there's more below" cue, exactly like the
 * reference site. The whole gallery is sticky so it stays in view
 * while the customer scrolls the product details on the right.
 * Clicking a thumbnail reuses fuzion-media-viewer's own existing
 * setActiveMedia() method (already used elsewhere in this theme) to
 * swap which image is showing - nothing about that method is changed.
 *
 * Mobile (< 992px): the existing swipeable single-image gallery is left
 * completely alone. This file only adds a row of dot indicators below
 * it (synced to the theme's own "slideChanged" event) and hides the
 * arrow buttons, so it swipes like a typical mobile image gallery.
 * ------------------------------------------------------------------ */

(function () {
  /* ---------------- Desktop thumbnail rail ---------------- */

  // Walks up from `el` to find the nearest real (non-transparent)
  // background color, so the bottom fade can blend into whatever the
  // page/section actually looks like (this theme supports per-section
  // color schemes) instead of being hardcoded to white.
  function getEffectiveBackgroundColor(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      var bg = getComputedStyle(node).backgroundColor;
      if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)") {
        return bg;
      }
      node = node.parentElement;
    }
    return getComputedStyle(document.body).backgroundColor || "#ffffff";
  }

  // Every media item is positioned absolutely, stacked directly on top of
  // one another (so switching the active one is a plain opacity crossfade,
  // never a layout jump) - see the .pdp-classic-view rules in
  // snippets/stylesheets.liquid. That means none of them are in normal
  // document flow, so the list itself has nothing to size its own height
  // from and collapses to 0 on its own. Shopify's image_tag already prints
  // each image's real source width/height as plain attributes, available
  // immediately without waiting for the image to finish loading, so this
  // gives the list a genuine CSS aspect-ratio box matching whichever image
  // is currently active - independent of any child's own flow/positioning,
  // and with nothing circular about it.
  function syncActiveAspectRatio(mediaList) {
    var activeItem =
      mediaList.querySelector(".product__media-item.is-active") ||
      mediaList.querySelector(".product__media-item");
    if (!activeItem) return;
    var media = activeItem.querySelector("img, model-viewer, video");
    if (!media) return;
    var w = parseFloat(media.getAttribute("width"));
    var h = parseFloat(media.getAttribute("height"));
    if (w > 0 && h > 0) {
      mediaList.style.aspectRatio = w + " / " + h;
    }
  }

  // The rail's height is set explicitly (in pixels) from the main image
  // column's own rendered height, rather than via CSS flex-stretch /
  // percentage heights. A flex container that isn't given an explicit
  // height sizes itself to fit its tallest child's own natural content -
  // and a rail with many thumbnails is "naturally" as tall as all of them
  // stacked, since percentage heights (height:100%) can't resolve against
  // an auto-height ancestor. That combination made the gallery box grow to
  // fit every thumbnail instead of clipping the rail to scroll, so every
  // image showed at once with no scrollbar. Setting a plain pixel height
  // directly avoids that circularity entirely.
  function syncRailHeight(mediaList, railWrap) {
    var h = mediaList.getBoundingClientRect().height;
    if (h > 0) railWrap.style.height = h + "px";
  }

  function updateRailOverflow(railWrap, rail) {
    var hasOverflow = rail.scrollHeight > rail.clientHeight + 1;
    railWrap.classList.toggle("has-rail-overflow", hasOverflow);
  }

  function buildDesktopGallery(viewerEl) {
    if (viewerEl.dataset.pdpGalleryBuilt === "true") return;

    // The whole enhancement is wrapped so that a failure on any one
    // product (unexpected markup, a missing image, etc.) can never leave
    // pdpGalleryBuilt permanently stuck at "true" without the rail having
    // actually been built - which would otherwise block every future
    // retry (e.g. shopify:section:load in the theme editor) forever.
    // Note the core desktop image show/hide behavior does NOT depend on
    // this flag or on this function succeeding at all - see the
    // .pdp-classic-view rules in snippets/stylesheets.liquid - so a
    // failure here only ever means "no thumbnail rail for this product",
    // never every image showing stacked at once.
    try {
      var mediaList = viewerEl.querySelector(".product__media-list");
      if (!mediaList) return;

      var items = Array.prototype.slice.call(
        mediaList.querySelectorAll(":scope > .product__media-item[data-media-id]"),
      );
      // Nothing to build a rail for (no images, or a single placeholder/image).
      if (items.length < 2) return;

      viewerEl.classList.add("pdp-gallery-sticky");

      var wrap = document.createElement("div");
      wrap.className = "pdp-gallery-wrap";

      var railWrap = document.createElement("div");
      railWrap.className = "pdp-thumb-rail-wrap";

      var rail = document.createElement("div");
      rail.className = "pdp-thumb-rail";

      var fade = document.createElement("div");
      fade.className = "pdp-thumb-rail-fade";
      fade.style.setProperty("--pdp-fade-bg", getEffectiveBackgroundColor(viewerEl));

      items.forEach(function (item, index) {
        var mediaId = item.getAttribute("data-media-id");
        var img = item.querySelector("img");

        var thumbWrap = document.createElement("div");
        thumbWrap.className =
          "pdp-thumb" + (item.classList.contains("is-active") ? " is-active" : "");
        thumbWrap.dataset.target = mediaId;

        var btn = document.createElement("button");
        btn.type = "button";
        btn.setAttribute(
          "aria-label",
          "View image " + (index + 1) + " of " + items.length,
        );

        if (img) {
          var thumbImg = document.createElement("img");
          // Reuses the already-loaded main image (no extra network request).
          thumbImg.src = img.currentSrc || img.src;
          thumbImg.alt = "";
          thumbImg.loading = "lazy";
          // Carry over the source image's real width/height as intrinsic-size
          // hints. The thumbnail's on-screen box is always fixed by CSS
          // (.pdp-thumb), so this changes nothing about how it looks - it
          // only lets the browser reserve the right space for this image
          // before it finishes loading, instead of a layout shift once it does.
          var srcWidth = img.getAttribute("width");
          var srcHeight = img.getAttribute("height");
          if (srcWidth) thumbImg.setAttribute("width", srcWidth);
          if (srcHeight) thumbImg.setAttribute("height", srcHeight);
          btn.appendChild(thumbImg);
        } else {
          btn.textContent = index + 1;
        }

        btn.addEventListener("click", function () {
          if (typeof viewerEl.setActiveMedia === "function") {
            viewerEl.setActiveMedia(mediaId, false);
          }
        });

        thumbWrap.appendChild(btn);
        rail.appendChild(thumbWrap);
      });

      // Move the existing media list (untouched, still holding every original
      // image/video/model element and all of its bound behavior) into the new
      // wrapper as the "main stage", alongside the new rail.
      mediaList.classList.add("pdp-main-stage");
      var parent = mediaList.parentNode;
      parent.insertBefore(wrap, mediaList);
      railWrap.appendChild(rail);
      railWrap.appendChild(fade);
      wrap.appendChild(railWrap);
      wrap.appendChild(mediaList);

      var recheckOverflow = function () {
        syncActiveAspectRatio(mediaList);
        syncRailHeight(mediaList, railWrap);
        updateRailOverflow(railWrap, rail);
      };

      // Layout (and therefore the main image's height, which the rail
      // is capped to match) may not be final yet on this exact tick - a
      // couple of follow-up passes cover image load / font swap / etc.
      recheckOverflow();
      window.requestAnimationFrame(recheckOverflow);
      setTimeout(recheckOverflow, 250);
      mediaList.querySelectorAll("img").forEach(function (img) {
        if (!img.complete) img.addEventListener("load", recheckOverflow, { once: true });
      });

      // Keep the rail's highlighted thumbnail in sync with whichever item is
      // actually active - no matter what triggered the change (a click here,
      // a variant/color swatch elsewhere, etc.) - by watching the real
      // source of truth (the .is-active class) directly. A newly-active item
      // can also change the main image's height (different aspect ratio),
      // which changes how much the rail needs to scroll, so re-check then too.
      var observer = new MutationObserver(function () {
        var activeItem = mediaList.querySelector(".product__media-item.is-active");
        var activeId = activeItem ? activeItem.getAttribute("data-media-id") : null;
        var activeThumb = null;
        rail.querySelectorAll(".pdp-thumb").forEach(function (thumb) {
          var isActive = thumb.dataset.target === activeId;
          thumb.classList.toggle("is-active", isActive);
          if (isActive) activeThumb = thumb;
        });
        if (activeThumb) {
          activeThumb.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
        window.requestAnimationFrame(recheckOverflow);
      });
      observer.observe(mediaList, {
        attributes: true,
        attributeFilter: ["class"],
        subtree: true,
      });

      if (!window.__pdpRailResizeBound) {
        window.__pdpRailResizeBound = true;
        var resizeTimer;
        window.addEventListener("resize", function () {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(function () {
            document.querySelectorAll(".pdp-thumb-rail-wrap").forEach(function (rw) {
              var r = rw.querySelector(".pdp-thumb-rail");
              var galleryWrap = rw.parentElement;
              var mainStage = galleryWrap ? galleryWrap.querySelector(".pdp-main-stage") : null;
              if (mainStage) syncRailHeight(mainStage, rw);
              if (r) updateRailOverflow(rw, r);
            });
          }, 150);
        });
      }

      // Only mark this gallery "built" now that every step above actually
      // succeeded, so a partial failure never permanently blocks a retry.
      viewerEl.dataset.pdpGalleryBuilt = "true";
    } catch (err) {
      if (window.console && console.warn) {
        console.warn("[pdp-gallery] failed to build desktop thumbnail rail:", err);
      }
    }
  }

  function initAllDesktopGalleries() {
    document
      .querySelectorAll('fuzion-media-viewer.mobile_hide_css.row[id^="MediaGallery-"]')
      .forEach(buildDesktopGallery);
  }

  /* ---------------- Mobile dot indicators ---------------- */

  function buildMobileDots(viewerEl) {
    if (viewerEl.dataset.pdpDotsBuilt === "true") return;

    try {
      var slider = viewerEl.querySelector(".mobile_pdp_slider");
      if (!slider) return;

      var slides = Array.prototype.slice.call(slider.querySelectorAll(".swiper-slide"));
      if (slides.length < 2) return;

      var dots = document.createElement("div");
      // desktop_hide_css matches the same class already on .mobile_pdp_slider
      // (see sections/product-information.liquid) - without it this element
      // has no rule hiding it above the 991px breakpoint (the CSS in
      // snippets/stylesheets.liquid only *styles* .pdp-mobile-dots for
      // mobile, it never hides it for desktop), so the dots rendered on
      // every screen size, appearing as a stray row of small dark circles
      // wherever this div happened to land in the desktop layout.
      dots.className = "pdp-mobile-dots desktop_hide_css";

      slides.forEach(function (slide, index) {
        var dot = document.createElement("button");
        dot.type = "button";
        dot.className =
          "pdp-dot" + (slide.classList.contains("is-active") ? " is-active" : "");
        dot.setAttribute("aria-label", "Go to image " + (index + 1));
        dot.addEventListener("click", function () {
          // Standard, public Swiper API - nothing about the slider itself
          // is modified or reconfigured here.
          if (slider.swiper) slider.swiper.slideTo(index);
        });
        dots.appendChild(dot);
      });

      slider.parentNode.insertBefore(dots, slider.nextSibling);
      bindSwiperSync(slider, dots);

      // Only mark as built once the dots actually made it into the DOM,
      // matching the same failure-safe pattern as buildDesktopGallery.
      viewerEl.dataset.pdpDotsBuilt = "true";
    } catch (err) {
      if (window.console && console.warn) {
        console.warn("[pdp-gallery] failed to build mobile dots:", err);
      }
    }
  }

  // Keeps the dots synced to whichever slide is actually active, straight
  // from the Swiper instance's own "slideChange" event (a standard, always-
  // fired Swiper API - unlike this theme's custom "slideChanged" DOM event,
  // which only fires after a CSS transition end and wasn't firing reliably
  // in testing). Safe to call repeatedly; only binds once per slider.
  function bindSwiperSync(slider, dots) {
    if (!slider.swiper || slider.dataset.pdpSyncBound === "true") return;
    slider.dataset.pdpSyncBound = "true";
    slider.swiper.on("slideChange", function (swiper) {
      dots.querySelectorAll(".pdp-dot").forEach(function (dot, i) {
        dot.classList.toggle("is-active", i === swiper.activeIndex);
      });
    });
  }

  function initAllMobileDots() {
    document
      .querySelectorAll('fuzion-media-viewer[id^="MediaGallery-Mobile-"]')
      .forEach(function (viewerEl) {
        buildMobileDots(viewerEl);
        // Swiper may not have finished initializing on the first pass;
        // this event fires once it has (see swiper-slider-component.js).
        var slider = viewerEl.querySelector(".mobile_pdp_slider");
        var dots = viewerEl.querySelector(".pdp-mobile-dots");
        if (slider && dots) bindSwiperSync(slider, dots);
      });
  }

  /* ---------------- Wiring ---------------- */

  function initAll() {
    initAllDesktopGalleries();
    initAllMobileDots();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Swiper for the mobile slider loads and initializes asynchronously;
  // this event fires once it's actually ready (see swiper-slider-component.js).
  document.addEventListener("fuzion-product-gallery-swiper-ready", initAllMobileDots);

  // Theme editor re-renders a section's markup fresh from the server on
  // save, which doesn't include anything this file built - re-run so the
  // rebuilt section gets its rail/dots again.
  document.addEventListener("shopify:section:load", function () {
    setTimeout(initAll, 0);
  });
})();